"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { TestCategory, TestDefinition } from "@test-platform/shared";
import { toSortedCompat } from "@/lib/array-compat";
import type { Locale } from "@/lib/i18n";
import { filterExplorerTests, readExplorerFilters } from "@/lib/test-explorer-filters";
import Icon, { type IconName } from "./Icon";
import TestCard from "./TestCard";
import HomeExplorerShortcuts from "./HomeExplorerShortcuts";
export type TestExplorerItem = Pick<TestDefinition, "id" | "title" | "description" | "category" | "createdAt"> & {
  isNew: boolean;
  questionCount: number;
};
type Props = {
  tests: TestExplorerItem[];
  counts?: Record<string, number>;
  locale?: Locale;
  homeContent?: ReactNode;
  compactCategories?: boolean;
};
const CATEGORY_VISUALS: Record<TestCategory, {
  icon: IconName;
  gradient: string;
  tagline: Record<Locale, string>;
}> = {
  personality: {
    icon: "brain",
    gradient: "from-fuchsia-400 via-pink-400 to-orange-300",
    tagline: {
      ko: "MBTI · 자기이해 · 성향",
      en: "MBTI · self-discovery · traits"
    }
  },
  love: {
    icon: "heart",
    gradient: "from-rose-500 via-pink-400 to-amber-300",
    tagline: {
      ko: "연애 · 호감 · 케미",
      en: "Dating · crushes · chemistry"
    }
  },
  emotion: {
    icon: "heart-pulse",
    gradient: "from-rose-400 via-orange-300 to-sky-300",
    tagline: {
      ko: "감정 · 위로 · 반응",
      en: "Emotion · comfort · reactions"
    }
  },
  taste: {
    icon: "palette",
    gradient: "from-cyan-400 via-fuchsia-300 to-amber-300",
    tagline: {
      ko: "취향 · 무드 · 감성",
      en: "Taste · mood · style"
    }
  },
  fun: {
    icon: "sparkles",
    gradient: "from-violet-400 via-sky-400 to-emerald-300",
    tagline: {
      ko: "가볍게 즐기는 테스트",
      en: "Quick quizzes for fun"
    }
  },
  career: {
    icon: "target",
    gradient: "from-indigo-500 via-violet-500 to-sky-400",
    tagline: {
      ko: "직장 · 일하는 방식",
      en: "Work · habits · style"
    }
  },
  food: {
    icon: "utensils",
    gradient: "from-amber-400 via-orange-400 to-rose-300",
    tagline: {
      ko: "음식 · 입맛 · 취향",
      en: "Food · taste · cravings"
    }
  },
  lifestyle: {
    icon: "sun",
    gradient: "from-emerald-400 via-teal-400 to-sky-300",
    tagline: {
      ko: "라이프 · 일상 패턴",
      en: "Life · routines · patterns"
    }
  },
  social: {
    icon: "users",
    gradient: "from-sky-400 via-indigo-400 to-violet-400",
    tagline: {
      ko: "관계 · 대화 스타일",
      en: "Friends · social energy"
    }
  },
  money: {
    icon: "wallet",
    gradient: "from-lime-400 via-emerald-400 to-teal-400",
    tagline: {
      ko: "소비 · 돈 · 가치관",
      en: "Money · spending · values"
    }
  }
};
const TEST_CATEGORIES: Array<{
  id: TestCategory;
  label: Record<Locale, string>;
}> = [{
  id: "personality",
  label: {
    ko: "성격",
    en: "Personality"
  }
}, {
  id: "love",
  label: {
    ko: "연애",
    en: "Dating"
  }
}, {
  id: "emotion",
  label: {
    ko: "감정",
    en: "Emotion"
  }
}, {
  id: "taste",
  label: {
    ko: "취향",
    en: "Taste"
  }
}, {
  id: "fun",
  label: {
    ko: "재미",
    en: "Fun"
  }
}, {
  id: "career",
  label: {
    ko: "직장",
    en: "Work"
  }
}, {
  id: "food",
  label: {
    ko: "음식",
    en: "Food"
  }
}, {
  id: "lifestyle",
  label: {
    ko: "라이프",
    en: "Lifestyle"
  }
}, {
  id: "social",
  label: {
    ko: "관계",
    en: "Social"
  }
}, {
  id: "money",
  label: {
    ko: "소비",
    en: "Money"
  }
}];
const PAGE_SIZE = 12;
const QUERY_DEBOUNCE_MS = 220;
const NEW_CATEGORY_IDS = new Set<TestCategory>(["emotion", "taste"]);
export default function TestExplorer(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-40 rounded-2xl bg-white/60" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <Suspense fallback={t0}><TestExplorerContent {...props} /></Suspense>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function TestExplorerContent(t0) {
  const $ = _c(39);
  const {
    tests,
    counts,
    locale: t1,
    homeContent,
    compactCategories: t2
  } = t0;
  const locale = t1 === undefined ? "ko" : t1;
  const compactCategories = t2 === undefined ? false : t2;
  const {
    push,
    replace
  } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t3;
  if ($[0] !== searchParams) {
    t3 = readExplorerFilters(searchParams);
    $[0] = searchParams;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const filters = t3;
  const {
    category: urlCategory,
    query: urlQuery,
    shortOnly,
    normalizedQuery,
    isBrowsing
  } = filters;
  const filterKey = `${urlCategory ?? ""}:${normalizedQuery}:${shortOnly}`;
  let t4;
  if ($[2] !== filterKey) {
    t4 = {
      key: filterKey,
      visible: PAGE_SIZE
    };
    $[2] = filterKey;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [visibleState, setVisibleState] = useState(t4);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const sentinelRef = useRef(null);
  const visible = visibleState.key === filterKey ? visibleState.visible : PAGE_SIZE;
  let t5;
  if ($[4] !== pathname || $[5] !== searchParams) {
    t5 = function buildUrl(next) {
      const params = new URLSearchParams(searchParams.toString());
      if (next.cat !== undefined) {
        if (next.cat) {
          params.set("cat", next.cat);
        } else {
          params.delete("cat");
        }
      }
      if (next.q !== undefined) {
        const trimmed = (next.q ?? "").trim();
        if (trimmed) {
          params.set("q", trimmed);
        } else {
          params.delete("q");
        }
      }
      if (next.duration !== undefined) {
        if (next.duration) {
          params.set("duration", next.duration);
        } else {
          params.delete("duration");
        }
      }
      const qs = params.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    };
    $[4] = pathname;
    $[5] = searchParams;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const buildUrl = t5;
  let t6;
  if ($[7] !== buildUrl || $[8] !== replace) {
    t6 = function handleQueryChange(value) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        replace(buildUrl({
          q: value || null
        }), {
          scroll: false
        });
      }, QUERY_DEBOUNCE_MS);
    };
    $[7] = buildUrl;
    $[8] = replace;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleQueryChange = t6;
  const selectCategory = function selectCategory(cat) {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    push(buildUrl({
      cat,
      q: null
    }), {
      scroll: false
    });
  };
  let t7;
  if ($[10] !== buildUrl || $[11] !== push) {
    t7 = function clearFilters() {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      push(buildUrl({
        cat: null,
        q: null,
        duration: null
      }), {
        scroll: false
      });
    };
    $[10] = buildUrl;
    $[11] = push;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const clearFilters = t7;
  let t8;
  if ($[13] !== locale || $[14] !== tests) {
    t8 = TEST_CATEGORIES.reduce((items, category) => {
      const count = tests.filter(test => test.category === category.id).length;
      if (count > 0) {
        items.push({
          id: category.id,
          label: category.label[locale],
          count
        });
      }
      return items;
    }, []);
    $[13] = locale;
    $[14] = tests;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const categorySummaries = t8;
  let list;
  if ($[16] !== counts || $[17] !== filters || $[18] !== tests) {
    list = filterExplorerTests(tests, filters);
    if (counts) {
      let t9;
      if ($[20] !== counts) {
        t9 = (a, b) => (counts[b.id] ?? 0) - (counts[a.id] ?? 0);
        $[20] = counts;
        $[21] = t9;
      } else {
        t9 = $[21];
      }
      list = toSortedCompat(list, t9);
    }
    $[16] = counts;
    $[17] = filters;
    $[18] = tests;
    $[19] = list;
  } else {
    list = $[19];
  }
  const filtered = list;
  let t10;
  let t9;
  if ($[22] !== filterKey || $[23] !== filtered.length || $[24] !== isBrowsing || $[25] !== visible) {
    t9 = () => {
      if (!isBrowsing) {
        return;
      }
      const node = sentinelRef.current;
      if (!node) {
        return;
      }
      if (visible >= filtered.length) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisibleState(prev => ({
              key: filterKey,
              visible: Math.min((prev.key === filterKey ? prev.visible : PAGE_SIZE) + PAGE_SIZE, filtered.length)
            }));
          }
        }
      }, {
        rootMargin: "320px 0px"
      });
      observer.observe(node);
      return () => observer.disconnect();
    };
    t10 = [filterKey, isBrowsing, filtered.length, visible];
    $[22] = filterKey;
    $[23] = filtered.length;
    $[24] = isBrowsing;
    $[25] = visible;
    $[26] = t10;
    $[27] = t9;
  } else {
    t10 = $[26];
    t9 = $[27];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      const debounce = debounceRef;
      return () => {
        if (debounce.current) {
          clearTimeout(debounce.current);
        }
      };
    };
    t12 = [];
    $[28] = t11;
    $[29] = t12;
  } else {
    t11 = $[28];
    t12 = $[29];
  }
  useEffect(t11, t12);
  const slice = filtered.slice(0, visible);
  let t13;
  if ($[30] !== locale || $[31] !== urlCategory) {
    t13 = urlCategory ? TEST_CATEGORIES.find(c => c.id === urlCategory)?.label[locale] : null;
    $[30] = locale;
    $[31] = urlCategory;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const activeCategoryLabel = t13;
  const copy = locale === "ko" ? {
    searchResults: "\uAC80\uC0C9 \uACB0\uACFC",
    browseByCategory: "\uCE74\uD14C\uACE0\uB9AC\uB85C \uB458\uB7EC\uBCF4\uAE30",
    total: `총 ${tests.length}개 테스트`,
    count: `${filtered.length}개`,
    searchLabel: "\uD14C\uC2A4\uD2B8 \uAC80\uC0C9",
    searchPlaceholder: "\uD14C\uC2A4\uD2B8 \uC774\uB984\xB7\uD0A4\uC6CC\uB4DC\uB85C \uAC80\uC0C9",
    backToCategories: "\uCE74\uD14C\uACE0\uB9AC\uB85C",
    noResults: "\uC870\uAC74\uC5D0 \uB9DE\uB294 \uD14C\uC2A4\uD2B8\uAC00 \uC5C6\uC5B4\uC694. \uB2E4\uB978 \uD0A4\uC6CC\uB4DC\uB85C \uAC80\uC0C9\uD574 \uBCF4\uC138\uC694.",
    loadingMore: "\uB354 \uBD88\uB7EC\uC624\uB294 \uC911\u2026",
    categoryCount: _temp,
    newCategory: "\uC2E0\uADDC"
  } : {
    searchResults: "Search results",
    browseByCategory: "Browse by category",
    total: `${tests.length} quizzes`,
    count: `${filtered.length} quizzes`,
    searchLabel: "Search quizzes",
    searchPlaceholder: "Search by quiz name or keyword",
    backToCategories: "Categories",
    noResults: "No matching quizzes. Try another keyword.",
    loadingMore: "Loading more...",
    categoryCount: _temp2,
    newCategory: "New"
  };
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="pointer-events-none absolute inset-y-0 left-3 grid place-items-center text-slate-400"><Icon name="search" size={16} /></span>;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== handleQueryChange) {
    t15 = event => handleQueryChange(event.target.value);
    $[34] = handleQueryChange;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== compactCategories || $[37] !== locale) {
    t16 = compactCategories ? <HomeExplorerShortcuts locale={locale} /> : null;
    $[36] = compactCategories;
    $[37] = locale;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  return <section className="flex flex-col gap-4"><div className="flex items-baseline justify-between px-1"><h2 className="font-display text-lg text-slate-800">{isBrowsing ? copy.searchResults : compactCategories ? copy.searchLabel : copy.browseByCategory}</h2><span className="text-sm text-slate-600">{isBrowsing ? copy.count : copy.total}</span></div><label className="relative block"><span className="sr-only">{copy.searchLabel}</span>{t14}<input ref={inputRef} type="search" defaultValue={urlQuery} onChange={t15} placeholder={copy.searchPlaceholder} className="min-h-12 w-full rounded-2xl bg-white/90 py-3 pl-9 pr-4 text-base text-slate-800 ring-1 ring-brand-200 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500" /></label>{t16}{!isBrowsing ? homeContent : null}{isBrowsing ? <><div className="flex flex-wrap items-center gap-2 px-1 text-xs"><button type="button" onClick={clearFilters} className="inline-flex min-h-11 items-center gap-1 rounded-full bg-white/80 px-3 py-1 font-display text-brand-700 ring-1 ring-brand-100 transition hover:bg-white"><Icon name="arrow-left" size={12} /><span>{copy.backToCategories}</span></button>{activeCategoryLabel ? <span className="inline-flex items-center rounded-full bg-brand-gradient px-3 py-1 font-display text-white">{activeCategoryLabel}</span> : null}{normalizedQuery ? <span className="text-slate-500">“{normalizedQuery}”</span> : null}{shortOnly ? <span className="rounded-full bg-amber-50 px-3 py-1 text-amber-900">{locale === "ko" ? "\uC57D 1\uBD84" : "About 1 min"}</span> : null}</div>{filtered.length > 0 ? <div className="flex flex-col gap-4">{slice.map((test_0, idx) => <TestCard key={test_0.id} test={test_0} index={idx} locale={locale} />)}</div> : <p className="rounded-2xl bg-white/60 p-6 text-center text-sm text-slate-500 ring-1 ring-brand-100">{copy.noResults}</p>}{visible < filtered.length ? <div ref={sentinelRef} className="grid h-12 place-items-center text-sm text-slate-600">{copy.loadingMore}</div> : null}</> : compactCategories ? <div className="flex flex-col gap-3"><h2 className="px-1 font-display text-lg text-slate-800">{copy.browseByCategory}</h2><nav aria-label={copy.browseByCategory} className="flex flex-wrap gap-2">{categorySummaries.map(category_0 => <button key={category_0.id} type="button" onClick={() => selectCategory(category_0.id)} className="inline-flex min-h-11 items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-left text-sm text-slate-800 ring-1 ring-brand-200 hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"><span className="font-display">{category_0.label}</span><span className="text-slate-600">{copy.categoryCount(category_0.count)}</span></button>)}</nav></div> : <div className="grid gap-3 sm:grid-cols-2">{categorySummaries.map(category_1 => {
        const visual = CATEGORY_VISUALS[category_1.id];
        return <button key={category_1.id} type="button" onClick={() => selectCategory(category_1.id)} className="card group flex cursor-pointer items-center gap-3 text-left animate-floatIn"><div className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br ${visual.gradient} text-white shadow-soft`} aria-hidden={true}><Icon name={visual.icon} size={22} strokeWidth={1.75} /></div><div className="flex flex-1 flex-col"><h3 className="font-display text-base text-slate-900">{category_1.label}<span className="ml-1 text-xs text-slate-600">{copy.categoryCount(category_1.count)}</span>{NEW_CATEGORY_IDS.has(category_1.id) ? <span className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] text-brand-600 ring-1 ring-brand-100">{copy.newCategory}</span> : null}</h3><p className="text-sm text-slate-600">{visual.tagline[locale]}</p></div><span className="text-brand-400 transition group-hover:translate-x-1" aria-hidden={true}><Icon name="arrow-right" size={18} /></span></button>;
      })}</div>}</section>;
}
function _temp2(count_1) {
  return `${count_1}`;
}
function _temp(count_0) {
  return `${count_0}개`;
}